import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import {
  ArrowRight, UserPlus, LogIn, ClipboardList, MailCheck,
  BadgeCheck, Award, Network, Landmark, CreditCard,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import AfricaNendaLogo from '../components/AfricaNendaLogo';

const STEPS = [
  { icon: ClipboardList, title: 'Apply',        desc: 'Submit a short self-service application.' },
  { icon: MailCheck,     title: 'Verify',       desc: 'Confirm your email; AfricaNenda reviews your application.' },
  { icon: BadgeCheck,    title: 'Certify',      desc: 'Run the certification cases against the Sandbox.' },
  { icon: Award,         title: 'Get your certificate', desc: 'AfricaNenda signs off your report and issues a certificate anyone can check by its QR code.' },
];

// The order the portal itself uses, so the two read the same way.
const CORRIDORS = [
  { icon: Landmark,   name: 'FSPIOP',    desc: 'Mojaloop FSPIOP v1.1 — party lookup, quotes and transfers against the hub.' },
  { icon: Network,    name: 'ISO 20022', desc: 'pacs / acmt / camt message flows with XMLDSig signing, certified by observation.' },
  { icon: CreditCard, name: 'ISO 8583',  desc: 'A card link over TCP — sign-on, authorisations and reversals, in the dialect the switch speaks.' },
];

export default function Landing() {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return null;
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-blue-100">
      {/* top bar */}
      <header className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <AfricaNendaLogo className="h-9" />
          <span className="hidden sm:block text-xs text-gray-500 border-l border-gray-300 pl-3 leading-tight">
            IIPS Sandbox
          </span>
        </div>
        <Link to="/login" className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-700 hover:text-primary-700">
          <LogIn className="w-4 h-4" /> Sign in
        </Link>
      </header>

      {/* hero */}
      <section className="max-w-3xl mx-auto px-6 pt-10 pb-14 text-center">
        <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 tracking-tight">
          Prove your payment gateway<br className="hidden sm:block" /> is ready for the switch.
        </h1>
        <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
          A multi-corridor testbed for instant and inclusive payment systems. Test your FSPIOP,
          ISO 20022 or ISO 8583 integration against the Sandbox, and receive a signed readiness
          report and certificate from AfricaNenda.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link to="/register"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-primary-600 text-white font-medium hover:bg-primary-700 shadow-sm w-full sm:w-auto">
            <UserPlus className="w-5 h-5" /> Apply to join
          </Link>
          <Link to="/login"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-white text-primary-700 font-medium border border-primary-200 hover:bg-primary-50 w-full sm:w-auto">
            Sign in <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* how it works */}
      <section className="max-w-5xl mx-auto px-6 pb-14">
        <h2 className="text-center text-sm font-semibold text-gray-500 uppercase tracking-wide mb-6">How it works</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={s.title} className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 relative">
                <span className="absolute top-4 right-4 text-xs font-mono text-gray-300">0{i + 1}</span>
                <div className="w-10 h-10 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center mb-3">
                  <Icon className="w-5 h-5" />
                </div>
                <p className="font-semibold text-gray-900">{s.title}</p>
                <p className="text-sm text-gray-500 mt-1 leading-relaxed">{s.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* corridors */}
      <section className="max-w-5xl mx-auto px-6 pb-16">
        <h2 className="text-center text-sm font-semibold text-gray-500 uppercase tracking-wide mb-6">Three corridors</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {CORRIDORS.map(c => {
            const Icon = c.icon;
            return (
              <div key={c.name} className="bg-white rounded-xl border border-gray-100 shadow-sm p-6 flex items-start gap-4">
                <div className="w-11 h-11 rounded-lg bg-primary-600 text-white flex items-center justify-center shrink-0">
                  <Icon className="w-6 h-6" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{c.name}</p>
                  <p className="text-sm text-gray-500 mt-1 leading-relaxed">{c.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
        <div className="text-center mt-10">
          <Link to="/register"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-primary-600 text-white font-medium hover:bg-primary-700 shadow-sm">
            <UserPlus className="w-5 h-5" /> Apply to join
          </Link>
        </div>
      </section>

      <footer className="border-t border-gray-200/60 py-6">
        <p className="text-center text-xs text-gray-500">
          AfricaNenda Foundation · IIPS Sandbox
        </p>
      </footer>
    </div>
  );
}
